/**
 * Vibration feedback. On Android browsers this uses navigator.vibrate.
 * iPhone Safari has no vibration API; since iOS 18 a native "switch"
 * checkbox gives a short tick when toggled, which we use as a fallback.
 * In the store app (Phase 7) Capacitor's Haptics plugin replaces both via
 * setHapticsDriver().
 */
export type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'warning';

export type HapticsDriver = (kind: HapticKind) => void;

const PATTERNS: Record<HapticKind, number | number[]> = {
  light: 8,
  medium: 16,
  heavy: 30,
  success: [12, 60, 24],
  warning: [10, 40, 10],
};

let iosSwitch: HTMLLabelElement | null = null;

function iosTick(): void {
  if (!iosSwitch) {
    const label = document.createElement('label');
    label.style.cssText = 'position:fixed;left:-100px;top:0;opacity:0;pointer-events:none';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    label.appendChild(input);
    document.body.appendChild(label);
    iosSwitch = label;
  }
  iosSwitch.click();
}

const webDriver: HapticsDriver = (kind) => {
  if (typeof navigator.vibrate === 'function') navigator.vibrate(PATTERNS[kind]);
  else if (/iPhone|iPad/.test(navigator.userAgent)) {
    iosTick();
    if (kind === 'heavy' || kind === 'success') setTimeout(iosTick, 70);
  }
};

let driver: HapticsDriver = webDriver;
let lastAt = 0;
export let hapticsEnabled = true;

export function setHapticsDriver(d: HapticsDriver): void {
  driver = d;
}

export function setHapticsEnabled(on: boolean): void {
  hapticsEnabled = on;
}

export function haptic(kind: HapticKind): void {
  if (!hapticsEnabled) return;
  const now = performance.now();
  if (now - lastAt < 50) return;
  lastAt = now;
  try {
    driver(kind);
  } catch {
    /* never let feedback break the game */
  }
}
